import type { AdministrativeSettings, AndroidSettings } from '@/services/applicationSettingsService';

export type SettingsFieldKey = Exclude<keyof AdministrativeSettings | keyof AndroidSettings, 'id' | 'theme'> | 'themeId';
export interface SettingsField { key: SettingsFieldKey; kind: 'toggle' | 'number' | 'select' | 'text' | 'email' }
export interface SettingsSection { id: string; domain: 'administration' | 'android'; fields: SettingsField[] }
export const settingsSections: SettingsSection[] = [
  {
    "id": "branding",
    "domain": "administration",
    "fields": [
      {
        "key": "appName",
        "kind": "text"
      },
      {
        "key": "appSubtitle",
        "kind": "text"
      },
      {
        "key": "logoUrl",
        "kind": "text"
      },
      {
        "key": "logoCompactUrl",
        "kind": "text"
      },
      {
        "key": "faviconUrl",
        "kind": "text"
      },
      {
        "key": "primaryColor",
        "kind": "text"
      },
      {
        "key": "secondaryColor",
        "kind": "text"
      },
      {
        "key": "accentColor",
        "kind": "text"
      },
      {
        "key": "fontFamily",
        "kind": "text"
      }
    ]
  },
  {
    "id": "support",
    "domain": "administration",
    "fields": [
      {
        "key": "supportEmail",
        "kind": "email"
      },
      {
        "key": "legalUrl",
        "kind": "text"
      },
      {
        "key": "privacyUrl",
        "kind": "text"
      }
    ]
  },
  {
    "id": "modules",
    "domain": "administration",
    "fields": [
      {
        "key": "categoriesEnabled",
        "kind": "toggle"
      },
      {
        "key": "tvChannelsEnabled",
        "kind": "toggle"
      },
      {
        "key": "tvGuideEnabled",
        "kind": "toggle"
      },
      {
        "key": "radioStationsEnabled",
        "kind": "toggle"
      },
      {
        "key": "moviesEnabled",
        "kind": "toggle"
      },
      {
        "key": "tvShowsEnabled",
        "kind": "toggle"
      },
      {
        "key": "documentariesEnabled",
        "kind": "toggle"
      },
      {
        "key": "sportsEnabled",
        "kind": "toggle"
      },
      {
        "key": "concertsEnabled",
        "kind": "toggle"
      }
    ]
  },
  {
    "id": "tools",
    "domain": "administration",
    "fields": [
      {
        "key": "iptvProvidersEnabled",
        "kind": "toggle"
      },
      {
        "key": "flussonicServersEnabled",
        "kind": "toggle"
      },
      {
        "key": "channelAutomationsEnabled",
        "kind": "toggle"
      },
      {
        "key": "channelScrapingEnabled",
        "kind": "toggle"
      },
      {
        "key": "liveTvCountriesEnabled",
        "kind": "toggle"
      },
      {
        "key": "radioCountriesEnabled",
        "kind": "toggle"
      },
      {
        "key": "fullAddressEnabled",
        "kind": "toggle"
      },
      {
        "key": "adsEnabled",
        "kind": "toggle"
      },
      {
        "key": "reportsEnabled",
        "kind": "toggle"
      }
    ]
  },
  {
    "id": "catalog",
    "domain": "administration",
    "fields": [
      {
        "key": "premiereContentDays",
        "kind": "number"
      },
      {
        "key": "recentContentDays",
        "kind": "number"
      },
      {
        "key": "indexOnSaveOrUpdate",
        "kind": "toggle"
      }
    ]
  },
  {
    "id": "demos",
    "domain": "administration",
    "fields": [
      {
        "key": "maxDemoPerMonth",
        "kind": "number"
      },
      {
        "key": "demoDays",
        "kind": "number"
      }
    ]
  },
  {
    "id": "appearance",
    "domain": "android",
    "fields": [
      {
        "key": "layout",
        "kind": "select"
      },
      {
        "key": "themeId",
        "kind": "select"
      },
      {
        "key": "homeSlidesDuration",
        "kind": "number"
      },
      {
        "key": "homeSlidesIndicatorPosition",
        "kind": "select"
      }
    ]
  },
  {
    "id": "navigation",
    "domain": "android",
    "fields": [
      {
        "key": "changePasswordEnabled",
        "kind": "toggle"
      },
      {
        "key": "fullScreenEnterActsAsBack",
        "kind": "toggle"
      },
      {
        "key": "showChannelsOnCategoryFocus",
        "kind": "toggle"
      },
      {
        "key": "channelCategoriesExpanded",
        "kind": "toggle"
      },
      {
        "key": "categoryMenuOnPreviewMode",
        "kind": "toggle"
      },
      {
        "key": "numericButtonInCategoryMenuOnPreviewMode",
        "kind": "toggle"
      },
      {
        "key": "mixCategoriesAndCountriesAlphabetically",
        "kind": "toggle"
      },
      {
        "key": "removeCountryGrouping",
        "kind": "toggle"
      }
    ]
  },
  {
    "id": "movies",
    "domain": "android",
    "fields": [
      {
        "key": "moviesLastNYears",
        "kind": "number"
      },
      {
        "key": "moviesShowPremiere",
        "kind": "toggle"
      },
      {
        "key": "moviesShowRecentlyAdded",
        "kind": "toggle"
      }
    ]
  },
  {
    "id": "tvShows",
    "domain": "android",
    "fields": [
      {
        "key": "tvShowsLastNYears",
        "kind": "number"
      },
      {
        "key": "tvShowsShowPremiere",
        "kind": "toggle"
      },
      {
        "key": "tvShowsShowRecentlyAdded",
        "kind": "toggle"
      }
    ]
  },
  {
    "id": "documentaries",
    "domain": "android",
    "fields": [
      {
        "key": "documentariesLastNYears",
        "kind": "number"
      },
      {
        "key": "documentariesShowPremiere",
        "kind": "toggle"
      },
      {
        "key": "documentariesShowRecentlyAdded",
        "kind": "toggle"
      }
    ]
  },
  {
    "id": "sports",
    "domain": "android",
    "fields": [
      {
        "key": "sportsLastNYears",
        "kind": "number"
      },
      {
        "key": "sportsShowPremiere",
        "kind": "toggle"
      },
      {
        "key": "sportsShowRecentlyAdded",
        "kind": "toggle"
      }
    ]
  },
  {
    "id": "concerts",
    "domain": "android",
    "fields": [
      {
        "key": "concertsLastNYears",
        "kind": "number"
      },
      {
        "key": "concertsShowPremiere",
        "kind": "toggle"
      },
      {
        "key": "concertsShowRecentlyAdded",
        "kind": "toggle"
      }
    ]
  }
];
